/**
 * Square OAuth helpers.
 *
 * Centralises the construction of the Square authorize URL so we can't
 * accidentally ship a stale `state` value again. Every call generates a
 * fresh random value, persists it in sessionStorage, and the callback
 * handler is expected to read and compare it via `consumeSquareOAuthState`.
 *
 * Why sessionStorage: the CSRF state must survive the full-page redirect
 * back from connect.squareup.com but must not outlive the browser tab.
 * This matches Square's partner QA guidance (state must be "unguessable
 * value stored in cookie or local storage" and "uniquely generated per
 * connection attempt").
 */
import {environment} from '../../environments/environment';

const STORAGE_KEY = 'squareOAuthState';

function randomState(): string {
  // crypto.randomUUID is available in every browser Angular 11 targets.
  const c: any = (typeof crypto !== 'undefined') ? crypto : null;
  if (c && typeof c.randomUUID === 'function') {
    return c.randomUUID().replace(/-/g, '');
  }
  // Fallback: 128 bits from getRandomValues.
  const bytes = new Uint8Array(16);
  if (c && typeof c.getRandomValues === 'function') {
    c.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i++) { bytes[i] = Math.floor(Math.random() * 256); }
  }
  return Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Generates a new CSRF state token, stores it in sessionStorage, and
 * returns the Square authorize URL the caller should redirect the user to.
 *
 * Also enforces lowercase `session=false` — Square's OAuth endpoint treats
 * this parameter as case-sensitive and rejects `False`.
 */
export function buildSquareAuthorizeUrl(): string {
  const state = randomState();
  try {
    sessionStorage.setItem(STORAGE_KEY, state);
  } catch {
    // sessionStorage can throw in private-mode Safari; fall through and
    // let the callback-side validation reject the returned state.
  }
  const base = environment.squareAuthUrl;
  const clientId = environment.clientIdSquare;
  const scope = environment.squarePermissions;
  return `${base}?client_id=${clientId}&scope=${scope}&session=false&state=${state}`;
}

/**
 * Callback-side validation. Returns true iff the returned state matches
 * the one we stored. Always clears the stored value so a single token can
 * only validate once. Returns false if either value is missing.
 */
export function consumeSquareOAuthState(returnedState: string | null): boolean {
  let stored: string | null = null;
  try {
    stored = sessionStorage.getItem(STORAGE_KEY);
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    stored = null;
  }
  if (!stored || !returnedState) return false;
  return stored === returnedState;
}
